import React from "react";
import { Activity, TrendingUp, TrendingDown } from "lucide-react";
import type { EmailMonitoring } from "../../services/EmailHygieneService";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface EmailMonitoringHistoryProps {
  monitoring: EmailMonitoring;
}

const EmailMonitoringHistory: React.FC<EmailMonitoringHistoryProps> = ({
  monitoring,
}) => {
  const chartData = monitoring.analysisHistory.map((history) => ({
    timestamp: new Date(history.timestamp).toLocaleDateString(),
    score: history.score,
  }));

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-dark";
    if (score >= 60) return "text-yellow-DEFAULT";
    return "text-red-dark";
  };

  const getScoreTrend = () => {
    if (monitoring.analysisHistory.length < 2) return null;
    const latest =
      monitoring.analysisHistory[monitoring.analysisHistory.length - 1].score;
    const previous =
      monitoring.analysisHistory[monitoring.analysisHistory.length - 2].score;
    return {
      direction: latest >= previous ? "up" : "down",
      difference: Math.abs(latest - previous),
    };
  };

  const trend = getScoreTrend();

  return (
    <div className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-yellow-DEFAULT" />
          <h3 className="text-lg font-semibold">Monitoring History</h3>
        </div>
        {trend && (
          <div className="flex items-center gap-2">
            {trend.direction === "up" ? (
              <TrendingUp className="w-4 h-4 text-green-dark" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-dark" />
            )}
            <span
              className={
                trend.direction === "up" ? "text-green-dark" : "text-red-dark"
              }
            >
              {trend.difference.toFixed(1)} points
            </span>
          </div>
        )}
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-divider dark:stroke-divider-dark"
              opacity={0.5}
            />
            <XAxis
              dataKey="timestamp"
              className="text-text-secondary dark:text-text-secondaryDark"
              fontSize={12}
            />
            <YAxis
              domain={[0, 100]}
              className="text-text-secondary dark:text-text-secondaryDark"
              fontSize={12}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "var(--background-paper)",
                borderColor: "var(--divider)",
                borderRadius: "8px",
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="var(--yellow-DEFAULT)"
              strokeWidth={2}
              dot={{ fill: "var(--yellow-DEFAULT)" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 space-y-4">
        {monitoring.analysisHistory
          .slice(-3)
          .reverse()
          .map((history, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-background-panel dark:bg-background-panelDark rounded-lg"
            >
              <div>
                <div className="text-sm text-text-secondary">
                  {new Date(history.timestamp).toLocaleString()}
                </div>
                <div className="font-medium">{history.status}</div>
              </div>
              <div
                className={`text-2xl font-bold ${getScoreColor(history.score)}`}
              >
                {history.score}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default EmailMonitoringHistory;
